import { useCallback, useMemo } from 'react'
import { renderToString } from 'react-dom/server'

import TurndownService from 'turndown'

import { useGlobalFeatureFlags } from './use-feature-flags'

import { PlaybookType, QuickGuiddeType, Shape } from 'app/types'

import { copyToClipboard, isValidUrl, logToAnalytics } from 'modules'
import { firebaseConfig } from 'env'

import {
    Description,
    GifImageWithPlayButton,
    Header,
    PoweredByGuidde,
    getStepText
} from 'UI/Components'

type CreateEmbedCodeProps = {
    iframeWidth: string
    iframeHeight: string
    playbook: PlaybookType
    playbookTitle?: string
    responsive?: boolean
}

const getIframeURL = ({ id, isPlaylist }: PlaybookType) =>
    `https://embed.${firebaseConfig.authDomain}/${isPlaylist ? 'playlists' : 'playbooks'}/${id}`

const micEmoji = '&#127908;'

export const createEmbedCode = ({
    iframeWidth,
    iframeHeight,
    playbook,
    playbookTitle = 'Guidde embed video',
    responsive
}: CreateEmbedCodeProps) => {
    const common = `src="${getIframeURL(playbook)}"
        title="${playbookTitle}"
        frameborder="0"
        allowfullscreen`

    return responsive
        ? `<iframe
            width="100%"
            height="${iframeHeight}"
            max-width="${iframeWidth}"
            ${common}
        ></iframe>`
        : `<iframe
            width="${iframeWidth}"
            height="${iframeHeight}"
            ${common}
    ></iframe>`
}

type GenerateHTMLProps = {
    playbook: PlaybookType
    content: string
    enableNgnixLinks?: boolean
    width?: '100%' | '50%'
    headerSize?: number
    headerAlignment?: 'left' | 'center'
}
const generateHTML = ({
    playbook,
    content,
    enableNgnixLinks,
    width,
    headerSize,
    headerAlignment
}: GenerateHTMLProps) => {
    const { title, username, uploadedAt, duration, steps: initialSteps, id } = playbook

    const steps = initialSteps?.filter(step => step.kind === 'step') || []

    return `
        <div>
            ${renderToString(
                <Header
                    title={title}
                    userName={username}
                    uploadedAt={uploadedAt}
                    counter={steps?.length}
                    videoDuration={duration}
                    playbookId={id}
                    headerSize={headerSize}
                    headerAlignment={headerAlignment}
                />
            )}
            ${content}
            ${renderToString(<Description description={playbook.description} />)}
            ${createSteps({ steps, enableNgnixLinks, width })}
            ${renderToString(<PoweredByGuidde />)}
        </div>`
}

type GenerateAndCopyStyledHTMLProps = {
    playbook: PlaybookType
    content: string
    enableNgnixLinks?: boolean
    width?: '100%' | '50%'
}

const generateAndCopyStyledHTML = ({
    playbook,
    content,
    enableNgnixLinks,
    width
}: GenerateAndCopyStyledHTMLProps) => {
    const html = generateHTML({ playbook, content, enableNgnixLinks, width })

    const container = document.createElement('div')
    container.innerHTML = html
    container.style.position = 'fixed'
    container.style.pointerEvents = 'none'
    container.style.opacity = '0'
    document.body.appendChild(container)

    window?.getSelection()?.removeAllRanges()
    const range = document.createRange()
    range.selectNode(container)
    window?.getSelection()?.addRange(range)
    document.execCommand('copy')
    document.body.removeChild(container)

    logToAnalytics('copyHtmlClicked', {
        context: 'QG',
        playbookId: playbook.id
    })
}

type CopyHTMLWithEmbedCodeType = {
    playbook: PlaybookType
    width?: '100%' | '50%'
    headerSize?: number
    iframeWidth?: number
    responsive?: boolean
    headerAlignment?: 'left' | 'center'
}

export const useCopyHtml = () => {
    const { enableNgnixLinks } = useGlobalFeatureFlags()

    const copyMarkdown = useCallback(
        (playbook: PlaybookType) => {
            const content = renderToString(
                <GifImageWithPlayButton playbook={playbook} enableNgnixLinks={enableNgnixLinks} />
            )
            const html = generateHTML({ playbook, content, enableNgnixLinks })

            const turndownService = new TurndownService()
            const markdown = turndownService.turndown(html)

            return copyToClipboard(markdown)
        },
        [enableNgnixLinks]
    )

    const copyHTMLWithPlayButton = useCallback(
        async (playbook: PlaybookType) => {
            const content = renderToString(<GifImageWithPlayButton playbook={playbook} />)

            await generateAndCopyStyledHTML({ playbook, content, enableNgnixLinks })
        },
        [enableNgnixLinks]
    )

    const copyHTMLWithEmbedCode = useCallback(
        ({
            playbook,
            width,
            headerSize,
            iframeWidth,
            responsive,
            headerAlignment
        }: CopyHTMLWithEmbedCodeType) => {
            const embedCode = createEmbedCode({
                iframeWidth: `${iframeWidth ? iframeWidth : 700}px`,
                iframeHeight: '400px',
                playbook,
                responsive
            })
            const content = generateHTML({
                playbook,
                content: embedCode,
                enableNgnixLinks,
                width,
                headerSize,
                headerAlignment
            })

            return copyToClipboard(content)
        },
        [enableNgnixLinks]
    )

    return useMemo(
        () => ({
            copyHTMLWithPlayButton,
            copyHTMLWithEmbedCode,
            copyMarkdown
        }),
        [copyHTMLWithPlayButton, copyHTMLWithEmbedCode, copyMarkdown]
    )
}

type CreateStepsProps = {
    steps: Array<QuickGuiddeType>
    enableNgnixLinks?: boolean
    width?: '100%' | '50%'
}

const createSteps = ({ steps, enableNgnixLinks, width = '100%' }: CreateStepsProps) => {
    let res: Array<string> = []

    const textStyle = `width: ${width}; word-break: break-word;`
    const indexDelta = steps[0]?.kind === 'cover' ? 0 : 1

    const screenshotKey = enableNgnixLinks ? 'docPublicScreenshot' : 'docScreenshot'

    steps.flatMap((step: QuickGuiddeType, index: number) => {
        const hasBgImage = step.layers.find(
            layer => layer.type === Shape.Image && layer.isBackground
        )

        const audioNoteBlock = `<div style="margin-top:16px; margin-bottom:16px;${textStyle}">
              <p style="font-size:14px; color: rgba(9, 12, 16, 0.6)">${micEmoji}  ${step.audioNote?.text}</p>
          </div>`

        const stepNoteBlock = `<div style="margin-top:16px; margin-bottom:16px" >
              <p style="font-size:14px; color: rgba(9, 12, 16, 0.6); ${textStyle}">${step.note}</Typography>
          </div>`

        // For cover and ending step we only need to show audio note and nothing more

        if (step.kind === 'cover' || step.kind === 'end') {
            if (step.audioNote?.isIncludeTranscriptInDoc) res.push(audioNoteBlock)
            if (step.note) res.push(stepNoteBlock)

            return
        }

        const url = step.url ? new URL(step.url) : { origin: '#', host: '' }

        if (index === 0 + (steps[0]?.kind === 'cover' ? 1 : 0)) {
            if (url.host) {
                res.push(
                    `<h3 style="${textStyle};max-width:100%">
                  Go to <a href=${url.origin} target="_blank">${url.host}</a>
              </h3>`
                )
            }
        }

        const defaultTitle = `<h3 style="${textStyle};max-width:100%">${
            index + indexDelta
        }. ${getStepText(step)}</h3>`

        if (step.type === 'urlChange') {
            // take link from the title string if the one exist
            const parsedTitle = getStepText(step).split('"')
            const hasValidLink = parsedTitle.length >= 3 ? isValidUrl(parsedTitle[1]) : false

            if (!step.url || !hasValidLink) res.push(defaultTitle)
            else
                res.push(
                    `<h3 style="${textStyle};max-width:100%">${index + indexDelta}. ${parsedTitle
                        .filter(e => e !== parsedTitle[1])
                        .join('')}
                    <a href="${step.url.includes('http') ? '' : '//'}${url}">${parsedTitle[1]}</a>
                </h3>`
                )
        } else {
            res.push(defaultTitle)
        }

        // need to show audioNote only if checkbox was selected during audioNote creation
        if (step.audioNote?.isIncludeTranscriptInDoc) res.push(audioNoteBlock)

        if (step.note) res.push(stepNoteBlock)

        if (hasBgImage) {
            res.push(
                `<img width=${width} src="${step?.[screenshotKey] || step.drawnScreenshot}" alt="${
                    step.title
                }" />`
            )
        }
    })
    return res.join(' ')
}
